import React from 'react';
import {
  Box,
  Button,
  Collapse,
  IconButton,
  Skeleton,
  SxProps,
  Typography,
  useTheme,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import type { BnplPurchasingPowerResponse } from '../../queries/bnpl';
import { getBnplPurchasingPowerStyles } from './style';
import heroImage from '../../assets/shopping_hero.png';

function formatClpInteger(value: number): string {
  return new Intl.NumberFormat('es-CL', {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  }).format(value);
}

type BnplPurchasingPowerProps = {
  purchasingPowerData: BnplPurchasingPowerResponse['data'] | null;
  purchasingPowerLoading: boolean;
  purchasingPowerError: number | null;
  mutate: () => void;
  sxProps?: SxProps;
};

export default function BnplPurchasingPower({
  purchasingPowerData,
  purchasingPowerLoading,
  purchasingPowerError,
  mutate,
  sxProps = {},
}: BnplPurchasingPowerProps) {
  const theme = useTheme();
  const sx = getBnplPurchasingPowerStyles(theme);

  const [purchasingInfoOpen, setPurchasingInfoOpen] = React.useState(false);

  const isNotFound = purchasingPowerData === null;
  const amount = purchasingPowerData?.availableBalance ?? null;
  const isInvalidPayload = !purchasingPowerLoading
    && !purchasingPowerError
    && amount === null;
  const showError = Boolean(purchasingPowerError && !isNotFound) || isInvalidPayload;
  const formattedAmount = amount !== null ? formatClpInteger(amount) : null;

  const purchasingPowerCardSkeleton = (
    <Box sx={sx.cardSkeleton}>
      <Box sx={sx.cardInner}>
        <Box sx={sx.skeletonRow}>
          <Box sx={sx.skeletonInnerRow}>
            <Skeleton variant="circular" width={22} height={22} sx={sx.skeletonShimmer} />
            <Skeleton variant="rectangular" width={180} height={18} sx={sx.skeletonShimmer} />
          </Box>
          <Skeleton variant="circular" width={28} height={28} sx={sx.skeletonShimmer} />
        </Box>
        <Skeleton variant="rectangular" width={200} height={50} sx={sx.skeletonShimmerStrong} />
      </Box>
    </Box>
  );

  if (purchasingPowerLoading) {
    return <Box sx={{ ...sx.root, ...sxProps }}>{purchasingPowerCardSkeleton}</Box>;
  }

  const handleActivateLine = () => {
    window.location.href = 'https://creditos.banca.me/bnpl';
  };
  if (isNotFound) {
    return (
      <Box sx={{
        ...sx.root, ...sxProps, position: 'relative', overflow: 'hidden', borderRadius: '24px',
      }}
      >
        <Box
          component="img"
          src={heroImage}
          alt=""
          sx={sx.heroImage}
        />
        <Box sx={sx.gradient1} />
        <Box sx={{
          ...sx.emptyStateContainer, position: 'relative', zIndex: 2,
        }}
        >
          <Typography variant="h5" fontWeight="bold" sx={sx.emptyStateTypography}>
            Compra en cuotas y sin tarjetas con banca.me
          </Typography>
          <Typography variant="body2" fontWeight="regular" sx={sx.emptyStateTypography}>
            Activa tu poder de compra y paga en cuotas sin tarjetas
            en tus marcas favoritas
          </Typography>
          <Button variant="contained" color="secondary" onClick={handleActivateLine} sx={{ backgroundColor: theme.palette.white.main, minWidth: 'max-content' }}>
            Activar pago en cuotas sin tarjetas
          </Button>
        </Box>
      </Box>
    );
  }

  if (showError) {
    return (
      <Box sx={{ ...sx.root, ...sxProps }}>
        <Box
          component="section"
          aria-label="Poder de compra BNPL hoy"
          sx={{ ...sx.card, textAlign: 'center' }}
        >
          <Typography variant="body1" color="white.main" sx={{ marginBottom: 3 }}>
            No pudimos cargar tu Poder de Compra. Revisa tu conexión e inténtalo de nuevo.
          </Typography>
          <Button variant="outlined" color="primary" sx={{ height: '3em' }} onClick={() => mutate()}>
            Reintentar
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{
      ...sx.root, ...sxProps, position: 'relative', overflow: 'hidden',
    }}
    >
      <Box
        component="img"
        src={heroImage}
        alt=""
        sx={sx.heroImage}
      />
      <Box sx={sx.gradient2} />
      <Box
        component="section"
        aria-label="Poder de compra BNPL hoy"
        sx={sx.card}
      >
        <Box sx={sx.cardInner}>
          <Box sx={sx.headerRow}>
            <Box sx={sx.labelRow}>
              <ShoppingBagOutlinedIcon sx={sx.bagIcon} aria-hidden />
              <Typography variant="caption" sx={sx.labelCaption}>
                Poder de Compra Actual
              </Typography>
            </Box>
            <IconButton
              size="small"
              aria-label={
                purchasingInfoOpen
                  ? 'Ocultar información sobre el monto disponible hoy'
                  : 'Mostrar información sobre el monto disponible hoy'
              }
              aria-expanded={purchasingInfoOpen}
              aria-controls="bnpl-purchasing-power-info-panel"
              onClick={() => setPurchasingInfoOpen((open) => !open)}
              sx={sx.infoIconButton}
            >
              <InfoOutlinedIcon sx={sx.infoIcon} />
            </IconButton>
          </Box>

          <Collapse in={purchasingInfoOpen} timeout="auto" sx={sx.infoPanelCollapse}>
            <Box
              component="div"
              id="bnpl-purchasing-power-info-panel"
              role="region"
              aria-label="Detalle del monto disponible hoy"
              sx={sx.infoPanelInner}
            >
              <Typography component="p" color="white.main" variant="caption" sx={sx.infoPanelCaption}>
                Tu poder de compra se adapta a ti. Sube cuando mantienes un buen historial de pagos
                y baja si tienes deudas acumuladas, para ayudarte a mantener unas finanzas sanas.
              </Typography>
            </Box>
          </Collapse>

          <Typography sx={sx.amountTypography} component="h3" fontWeight="bold">
            $
            {' '}
            {formattedAmount}
          </Typography>
          <Button
            variant="text"
            size="small"
            sx={{
              color: 'white.main',
              textDecoration: 'underline',
              marginTop: 0,
              textTransform: 'none',
              width: '100%',
              justifyContent: 'center',
            }}
            onClick={() => window.open('https://banca.me/personas/cuotas-sin-tarjeta#:~:text=Mostrando%20', '_blank')}
          >
            Ver dónde puedo comprar
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

BnplPurchasingPower.defaultProps = {
  sxProps: {},
};
